import {
  useWeather,
  TemperatureUnit,
  WeatherConditions,
  WeatherRelativeHumidityLevel,
} from "common";
import { useMemo } from "react";

import { useAppSync } from "../../contexts/AppSyncContext";
import { WeatherData } from "../../types/Weather";

export const useGetWeatherData = () => {
  const { selectedHub, hubsLoading } = useAppSync();
  const { fetchWeather } = useWeather();

  const currentLocation = useMemo(
    () =>
      selectedHub
        ? {
            name: selectedHub.location || "",
            lat: selectedHub.latitude ? Number(selectedHub.latitude) : null,
            long: selectedHub.longitude ? Number(selectedHub.longitude) : null,
          }
        : null,
    [
      selectedHub?.id,
      selectedHub?.location,
      selectedHub?.latitude,
      selectedHub?.longitude,
    ],
  );

  const getWeather = async (): Promise<WeatherData | null> => {
    if (
      !currentLocation ||
      currentLocation.lat === null ||
      currentLocation.long === null
    ) {
      return null;
    }

    try {
      const weatherData = await fetchWeather(
        currentLocation.lat,
        currentLocation.long,
      );

      return {
        current: {
          temperature: weatherData.current.temperature.degrees,
          temperatureUnit:
            weatherData.current.temperature.unit === "FAHRENHEIT"
              ? TemperatureUnit.Fahrenheit
              : TemperatureUnit.Celsius,
          conditions: weatherData.current.conditionsType,
          highTemperature: weatherData.current.highTemperature.degrees,
          lowTemperature: weatherData.current.lowTemperature.degrees,
          uvIndex: weatherData.current.uvIndex,
          uvIndexLevel: weatherData.current.uvIndexLevel,
          airQuality: weatherData.current.airQuality,
          airQualityLevel: weatherData.current.airQualityLevel,
          relativeHumidity: weatherData.current.relativeHumidity,
          relativeHumidityLevel: weatherData.current.relativeHumidityLevel,
        },
        upcoming: {
          temperature: weatherData.upcoming.temperature.degrees,
          temperatureUnit:
            weatherData.upcoming.temperature.unit === "FAHRENHEIT"
              ? TemperatureUnit.Fahrenheit
              : TemperatureUnit.Celsius,
          conditions: weatherData.upcoming.conditionsType,
          forecast: weatherData.upcoming.forecast.map(
            (day: {
              date: string | number | Date;
              temperature: { degrees: number; unit: string };
              conditionsType: WeatherConditions;
              relativeHumidity: number;
              relativeHumidityLevel: WeatherRelativeHumidityLevel;
            }) => ({
              date: new Date(day.date),
              temperature: day.temperature.degrees,
              temperatureUnit:
                day.temperature.unit === "FAHRENHEIT"
                  ? TemperatureUnit.Fahrenheit
                  : TemperatureUnit.Celsius,
              conditions: day.conditionsType,
              relativeHumidity: day.relativeHumidity,
              relativeHumidityLevel: day.relativeHumidityLevel,
            }),
          ),
        },
      };
    } catch (error) {
      console.error("Error fetching weather data:", error);
      return null;
    }
  };

  return {
    getWeather,
    currentLocation,
    isLoading: hubsLoading,
  };
};
